/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { Fragment } from "react";
import get from "lodash.get";
import { StoryCard } from "../../Molecules/StoryCard";
import { HeroImage } from "../../Atoms/HeroImage";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { CollectionName } from "../../Atoms/CollectionName";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { getTextColor, getSlot } from "../../../utils/utils";
import { collectionToStories } from "@quintype/components";
import PropTypes from "prop-types";
import { StateProvider } from "../../SharedContext";
import "./three-col.m.css";

const ThreeColGrid = ({ collection, config = {}, getMoreStories, isLoadMoreVisible, isLoading }) => {
  const {
    collectionNameBorderColor = "",
    borderColor = "",
    theme = "",
    slotConfig = [],
    border = "",
    collectionNameTemplate = "",
    footerSlotConfig = {},
    footerButton = ""
  } = config;
  const storyItems = collectionToStories(collection);
  if (!storyItems.length) return null;

  const textColor = getTextColor(theme);
  const { footerSlot } = footerSlotConfig;
  const footerSlotComp = footerSlot ? footerSlot() : null;
  const { type = "", component } = get(slotConfig, [0], {});
  const adWidgetSlot = type === "ad" || type === "widget" ? getSlot(type, component) : null;

  const loadMore = async () => {
    if (isLoading) return; // prevent multiple clicks/calls
    const limit = 3;
    const offset = storyItems.length;
    await getMoreStories(offset, limit);
  };

  const storyCard = story => {
    return (
      <div styleName="cards">
        <StoryCard story={story} border={border} theme={theme} isHorizontalMobile>
          <HeroImage aspectRatio={[[16, 9]]} story={story} isHorizontalMobile />
          <StorycardContent theme={theme} border={border} story={story} isHorizontalMobile borderColor={borderColor} />
        </StoryCard>
      </div>
    );
  };

  return (
    <div className="full-width-with-padding arrow-component" style={{ backgroundColor: theme, color: textColor }}>
      <div styleName="three-col-grid">
        <CollectionName
          collection={collection}
          collectionNameTemplate={collectionNameTemplate}
          collectionNameBorderColor={collectionNameBorderColor}
        />
        <div styleName="wrapper">
          {storyItems.slice(0, 2).map((story, index) => {
            return <Fragment key={`story-3col1-${index}-${get(story, ["id"], "0")}`}>{storyCard(story)}</Fragment>;
          })}
          {storyItems.length < 2 && <div />}
          {adWidgetSlot}
          {storyItems.slice(2).map((story, index) => {
            return <Fragment key={`story-3col1-${index}-${get(story, ["id"], "0")}`}>{storyCard(story)}</Fragment>;
          })}
        </div>
        {isLoadMoreVisible && <LoadmoreButton collection={collection} template={footerButton} onClick={loadMore} />}

        {footerSlotComp}
      </div>
    </div>
  );
};

ThreeColGrid.propTypes = {
  /**  collection is the array of objects which is returning by API  */
  collection: PropTypes.object.isRequired,
  /** The config for the Row */
  config: PropTypes.shape({
    theme: PropTypes.string,
    border: PropTypes.bool,
    slotConfig: PropTypes.array,
    footerButton: PropTypes.string,
    collectionNameTemplate: PropTypes.string,
    collectionNameBorderColor: PropTypes.string
  }),
  getMoreStories: PropTypes.func,
  isLoadMoreVisible: PropTypes.bool,
  isLoading: PropTypes.bool
};

ThreeColGrid.defaultProps = {
  getMoreStories: () => {},
  isLoadMoreVisible: true,
  isLoading: false
};

export default StateProvider(ThreeColGrid);
